import { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Drill, Info, Lock, Pencil, Plus, Trash2 } from "lucide-react";
import { del, errorMessage, get } from "@/lib/api";
import { useAsync } from "@/lib/queries";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, ErrorState, LoadingRows, PageHeader } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MortiseLockDialog, lockImageUrl } from "@/components/MortiseLockDialog";


const ALL = "__all";

const FilterSelect = ({ label, value, onChange, options, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="w-full bg-white sm:w-48" aria-label={label} data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value={ALL}>All {label.toLowerCase()}s</SelectItem>
      {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
    </SelectContent>
  </Select>
);

const LockCard = ({ lock, canEdit, onEdit, onDelete }) => (
  <article className="flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-testid={`lock-card-${lock.id}`}>
    <div className="flex aspect-[4/3] items-center justify-center bg-slate-100">
      {lock.has_image ? <img src={lockImageUrl(lock)} alt={`${lock.brand} ${lock.model}`} loading="lazy" className="h-full w-full object-contain" /> : <Lock className="h-10 w-10 text-slate-300" />}
    </div>
    <div className="flex flex-1 flex-col gap-2 p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{lock.brand}</p>
          <h3 className="font-display text-lg font-bold text-slate-900" data-testid={`lock-card-model-${lock.id}`}>{lock.model}</h3>
        </div>
        <span className="font-data inline-flex shrink-0 items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-bold text-blue-800" data-testid={`lock-card-bit-${lock.id}`}>
          <Drill className="h-3.5 w-3.5" /> Bit {lock.drill_bit}
        </span>
      </div>
      <span className="w-fit rounded border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-700">{lock.colour}</span>
      {lock.identification && <p className="whitespace-pre-line text-sm text-slate-700" data-testid={`lock-card-identification-${lock.id}`}><span className="font-semibold text-slate-900">Identification: </span>{lock.identification}</p>}
      {lock.drill_point && <p className="whitespace-pre-line text-sm text-slate-700" data-testid={`lock-card-drill-point-${lock.id}`}><span className="font-semibold text-slate-900">Drill point: </span>{lock.drill_point}</p>}
      {canEdit && (
        <div className="mt-auto flex justify-end gap-1 pt-1">
          <Button size="icon" variant="ghost" aria-label="Edit lock" onClick={() => onEdit(lock)} data-testid={`lock-edit-${lock.id}`}><Pencil className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" aria-label="Delete lock" onClick={() => onDelete(lock)} data-testid={`lock-delete-${lock.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
        </div>
      )}
    </div>
  </article>
);

export default function MortiseLocks() {
  const { profile } = useAuth();
  const userTypes = profile?.user_types?.length ? profile.user_types : [profile?.role];
  const canView = profile?.role === "manager" || userTypes.includes("locksmith");
  const canEdit = !!profile?.is_platform_owner;
  const { data, loading, error, reload } = useAsync(() => canView ? get("/mortise-locks") : Promise.resolve([]), [canView]);
  const [brand, setBrand] = useState(ALL);
  const [colour, setColour] = useState(ALL);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(undefined);
  const locks = useMemo(() => data || [], [data]);
  const brands = useMemo(() => [...new Set(locks.map((l) => l.brand))].sort(), [locks]);
  const colours = useMemo(() => [...new Set(locks.map((l) => l.colour))].sort(), [locks]);
  const shown = locks.filter((l) => (brand === ALL || l.brand === brand) && (colour === ALL || l.colour === colour)
    && (!q.trim() || `${l.brand} ${l.model} ${l.identification || ""}`.toLowerCase().includes(q.trim().toLowerCase())));

  if (!canView) return <Navigate to="/jobs" replace />;

  const remove = async (l) => {
    if (!window.confirm(`Delete ${l.brand} ${l.model}?`)) return;
    try {
      await del(`/mortise-locks/${l.id}`);
      toast.success("Lock deleted");
      reload();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="mortise-locks-page">
      <PageHeader title="Mortise Locks" description="Drill points and drill bits for common mortise locks."
        actions={canEdit && <Button onClick={() => setEditing(null)} className="bg-blue-700 hover:bg-blue-800" data-testid="add-lock-button"><Plus className="mr-2 h-4 w-4" />Add lock</Button>} />
      <div role="status" className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="locks-notice">
        <Info className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
        <p><strong>New locks are being added daily.</strong> Please bear with us while we build up the list.</p>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        <Input placeholder="Search brand, model or identification" value={q} onChange={(e) => setQ(e.target.value)} className="bg-white sm:w-64" data-testid="lock-search-input" />
        <FilterSelect label="Brand" value={brand} onChange={setBrand} options={brands} testId="lock-brand-filter" />
        <FilterSelect label="Colour" value={colour} onChange={setColour} options={colours} testId="lock-colour-filter" />
      </div>
      {loading && <LoadingRows />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data && !shown.length && <EmptyState icon={Lock} title={locks.length ? "No locks match" : "No locks yet"} description={locks.length ? "Try a different brand or colour." : canEdit ? "Add the first mortise lock." : "Locks will appear here once they're added."} testId="locks-empty" />}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="lock-grid">
        {shown.map((l) => <LockCard key={l.id} lock={l} canEdit={canEdit} onEdit={setEditing} onDelete={remove} />)}
      </div>
      {canEdit && <MortiseLockDialog open={editing !== undefined} onOpenChange={(o) => !o && setEditing(undefined)} lock={editing} locks={locks} onSaved={reload} />}
    </div>
  );
}
